/**
 * Break Buffer Helpers
 * 
 * Utilities for unified break-buffer logic across running and paused tickets.
 * Handles Case A (remainingMinutes >= breakDuration) and Case B (remainingMinutes < breakDuration).
 */

import { supabase } from '@/integrations/supabase/client';
import { calculateWallMinutes, parseTimeToMinutes } from './canonicalTimeMath';

/**
 * Get elapsed timer minutes for a ticket from all timer sessions
 * Sums all completed session duration_min values
 */
export async function getElapsedTimerMinutes(ticketId: string): Promise<number> {
  const { data: sessions, error } = await supabase
    .from('ticket_timer_sessions')
    .select('duration_min')
    .eq('ticket_id', ticketId)
    .not('stopped_at', 'is', null);

  if (error || !sessions) {
    console.warn('[BreakBuffer] Failed to fetch timer sessions:', error);
    return 0;
  }

  let totalMinutes = 0;
  for (const session of sessions) {
    totalMinutes += session.duration_min || 0;
  }

  return totalMinutes;
}

/**
 * Calculate remaining work minutes for a ticket
 * remainingMinutes = max(0, fullWallMinutes - elapsedTimerMinutes)
 */
export async function calculateRemainingMinutes(
  ticketId: string,
  predictedMinutes: number,
  efficiencyPct: number,
  bufferPct: number
): Promise<number> {
  const fullWallMinutes = calculateWallMinutes(predictedMinutes, efficiencyPct, bufferPct);
  const elapsedMinutes = await getElapsedTimerMinutes(ticketId);
  return Math.max(0, fullWallMinutes - elapsedMinutes);
}

// Core-100 R4 (CORE-AC): the pure break-buffer rules live ONCE, beside the server-side finish
// derivations that now apply them (supabase/functions/_shared/finishIntentDerivation.ts). This module
// keeps its API by re-exporting them; the browser no longer writes a finish with them.
export {
  parseBreaksForDate,
  findBreakOverlap,
  ticketSpansBreak,
  computeBreakBufferAction,
  consumeBreakBuffer,
  type BreakBufferResult,
} from '../../supabase/functions/_shared/finishIntentDerivation';

/**
 * Get minutes from midnight for a given ISO timestamp in shop timezone
 */
export function getMinutesFromMidnight(isoTimestamp: string, timezone: string): number {
  try {
    const date = new Date(isoTimestamp);
    // Simple approximation - for accurate results, use date-fns-tz
    const options: Intl.DateTimeFormatOptions = { 
      timeZone: timezone, 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    };
    const timeStr = date.toLocaleTimeString('en-US', options);
    return parseTimeToMinutes(timeStr);
  } catch {
    return 0;
  }
}
